import { alpha, Box, Stack, Typography } from '@mui/material';

interface EmptyStateExamples {
  title: string;
  items: string[];
}

interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  accentColor: string;
  action?: React.ReactNode;
  /**
   * When provided, renders the richer "Page Clarity" layout: a 64px gradient
   * circle, a dashed-border box with the example chips, and the action below.
   * When omitted, renders the compact default layout.
   */
  examples?: EmptyStateExamples;
  /** Max width applied to description and examples box in the clarity layout. */
  contentMaxWidth?: number;
  /** Marks the icon circle as decorative for assistive technology. */
  iconAriaHidden?: boolean;
}

export function EmptyState({
  icon,
  title,
  description,
  accentColor,
  action,
  examples,
  contentMaxWidth = 360,
  iconAriaHidden,
}: EmptyStateProps) {
  if (examples) {
    return (
      <ClarityEmptyState
        icon={icon}
        title={title}
        description={description}
        accentColor={accentColor}
        action={action}
        examples={examples}
        contentMaxWidth={contentMaxWidth}
        iconAriaHidden={iconAriaHidden}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 8,
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box
        aria-hidden={iconAriaHidden}
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${alpha(accentColor, 0.15)}, ${alpha(accentColor, 0.05)})`,
          mb: 2,
          '& .MuiSvgIcon-root': {
            fontSize: 28,
            color: accentColor,
          },
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            maxWidth: 280,
            mb: action ? 4 : 0,
          }}
        >
          {description}
        </Typography>
      )}
      {action}
    </Box>
  );
}

function ClarityEmptyState({
  icon,
  title,
  description,
  accentColor,
  action,
  examples,
  contentMaxWidth,
  iconAriaHidden,
}: Required<Pick<EmptyStateProps, 'icon' | 'title' | 'accentColor' | 'examples'>> &
  Pick<EmptyStateProps, 'description' | 'action' | 'iconAriaHidden'> & {
    contentMaxWidth: number;
  }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: { xs: 1.5, sm: 2 },
        pb: { xs: 4, sm: 5 },
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box
        aria-hidden={iconAriaHidden}
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${alpha(accentColor, 0.18)}, ${alpha(accentColor, 0.04)})`,
          mb: 2,
          '& .MuiSvgIcon-root': {
            fontSize: 32,
            color: accentColor,
          },
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            maxWidth: contentMaxWidth,
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          {description}
        </Typography>
      )}
      <Box
        sx={{
          width: '100%',
          maxWidth: contentMaxWidth,
          p: 2,
          mb: action ? 3 : 0,
          borderRadius: 2,
          border: '1px dashed',
          borderColor: alpha(accentColor, 0.3),
          bgcolor: alpha(accentColor, 0.04),
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: accentColor,
            mb: 1,
          }}
        >
          {examples.title}
        </Typography>
        <Stack
          direction="row"
          spacing={0.75}
          useFlexGap
          sx={{
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {examples.items.map((label) => (
            <Box
              key={label}
              component="span"
              sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                fontSize: '0.78rem',
                fontWeight: 600,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: alpha(accentColor, 0.25),
                color: 'text.primary',
              }}
            >
              {label}
            </Box>
          ))}
        </Stack>
      </Box>
      {action && (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>{action}</Box>
      )}
    </Box>
  );
}
